import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import * as SubmissionActions from './submission.actions';
import * as QuestionnaireActions from './questionnaire.actions';
import {
  MessagesService,
  QuestionnaireService,
  ReviewerMaterialsService,
  SiteService,
  SubmissionsService,
} from '../services';
import { selectSubmission } from './submission.selectors';
import { Store } from '@ngrx/store';
import { Submission, SubmissionResponse } from '../models';

import { convertKeysToCamelCase } from '@shared/utils';

import { MatDialog } from '@angular/material/dialog';
import { IRBPlus, SUBMISSION_HEADER_ACTIONS } from '@data-access/submissions';

@Injectable()
export class SubmissionEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _reviewerDocumentsService = inject(ReviewerMaterialsService);
  private readonly _messageService = inject(MessagesService);
  private readonly _store = inject(Store);
  private readonly _siteService = inject(SiteService);
  private readonly _submissionsService = inject(SubmissionsService);
  private readonly _questionnaireService = inject(QuestionnaireService);
  private readonly _matDialog = inject(MatDialog);

  setReviewMaterialDocuments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.setReviewMaterialDocuments),
      filter((action) => action.document.isComplete !== undefined),
      switchMap((action) =>
        this._reviewerDocumentsService
          .toggleCompleteReviewDocument(
            action.document.id,
            action.document.isComplete,
          )
          .pipe(
            map(() =>
              SubmissionActions.setReviewMaterialDocumentsSuccess({
                ...action,
              }),
            ),
            catchError((error) =>
              of(
                SubmissionActions.setReviewMaterialDocumentsFailure({ error }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteReviewMaterialDocuments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.deleteReviewMaterialDocuments),
      switchMap((action) =>
        this._handleSubmissionAndCallService(
          action,
          (submissionId, action) => {
            if (
              !this._reviewerDocumentsService.checkIfDocumentExists(
                action.documentId,
              )
            ) {
              return this._reviewerDocumentsService.getReviewerDocumentsData(
                submissionId,
              );
            }
            // Return an empty array instead of a string
            return of([]); // empty array of documents
          },
          (_, action) =>
            SubmissionActions.deleteReviewMaterialDocumentsSuccess({
              ...action,
            }),
          (error) =>
            SubmissionActions.deleteReviewMaterialDocumentsFailure({ error }),
        ),
      ),
    ),
  );

  addToThreadList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.addToThreadList),
      switchMap((action) =>
        this._handleSubmissionAndCallService(
          action,
          (submissionId) => this._messageService.getMessages(submissionId),
          (_, action) =>
            SubmissionActions.addToThreadListSuccess({ ...action }),
          (error) => SubmissionActions.addToThreadListFailure({ error }),
        ),
      ),
    ),
  );

  replytoThread$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.replytoThread),
      switchMap((action) => {
        return this._handleSubmissionAndCallService(
          action,
          (submissionId) => this._messageService.getMessages(submissionId),
          (_, action) => SubmissionActions.replytoThreadSuccess({ ...action }),
          (error) => SubmissionActions.replytoThreadFailure({ error }),
        );
      }),
    ),
  );

  reloadEntitysToThread$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.reloadEntitysToThread),
      switchMap((action) => {
        return this._handleSubmissionAndCallService(
          action,
          (submissionId) => this._messageService.getMessages(submissionId),
          (_, action) => SubmissionActions.replytoThreadSuccess({ ...action }),
          (error) => SubmissionActions.replytoThreadFailure({ error }),
        );
      }),
    ),
  );

  updateAnswerThroughSocket$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuestionnaireActions.updateAnswerThroughSocket),
      switchMap((action) => {
        return this._handleSubmissionAndCallService(
          action,
          (_) => this._questionnaireService.reloadQuestionnaire(),
          () => SubmissionActions.submissionNoop(),
          () => SubmissionActions.submissionNoop(),
        );
      }),
    ),
  );

  requestDocumentThreadCreationSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.requestDocumentThreadCreationSuccess),
      switchMap((action) => {
        return this._handleSubmissionAndCallService(
          action,
          (submissionId) => this._messageService.getMessages(submissionId),
          () => SubmissionActions.submissionNoop(),
          () => SubmissionActions.submissionNoop(),
        );
      }),
    ),
  );

  respondDocumentRequestThread$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.respondDocumentRequestThread),
      switchMap((action) =>
        this._handleSubmissionAndCallService(
          action,
          (submissionId) => this._messageService.getMessages(submissionId),
          (_, action) =>
            SubmissionActions.respondDocumentRequestThreadSuccess({
              ...action,
            }),
          (error) =>
            SubmissionActions.respondDocumentRequestThreadFailure({ error }),
        ),
      ),
    ),
  );

  closeActiveThread$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.closeActiveThread),
      switchMap(() =>
        this._handleSubmissionAndCallService(
          { type: 'closeActiveThread' }, // No action to pass here
          (submissionId) => this._messageService.getMessages(submissionId),
          () => SubmissionActions.submissionNoop(),
          () => SubmissionActions.submissionNoop(),
        ),
      ),
    ),
  );

  addSiteToSubmission$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.addSitesToSubmission),
      switchMap((action) =>
        this._siteService
          .addSiteToSubmission(
            action.submissionId,
            action.siteIds,
            action.reason,
          )
          .pipe(
            map(() => SubmissionActions.addSitesToSubmissionSuccess()),
            catchError((error) =>
              of(SubmissionActions.addSitesToSubmissionFailure({ error })),
            ),
          ),
      ),
    ),
  );

  getSubmissionSites$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.getSubmissionSites),
      switchMap(({ submissionId, queryParams, ignoreHeaders }) =>
        this._siteService.getSubmissionSites(submissionId, queryParams).pipe(
          map((response) =>
            SubmissionActions.getSubmissionSitesSuccess({
              sites: convertKeysToCamelCase(response.data),
              statuses: response.statuses,
              ignoreHeaders: ignoreHeaders || false,
              total: response.total,
            }),
          ),
          catchError((error) =>
            of(
              SubmissionActions.getSubmissionSitesFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getSitesForStudy$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.getSitesForStudy),
      switchMap(({ studyId, queryParams }) =>
        this._siteService.getSitesForStudyByRegion(studyId, queryParams).pipe(
          map((response) => {
            return SubmissionActions.getStudySitesSuccess({
              sites: convertKeysToCamelCase(response.data),
              statuses: response.statuses,
              total: response.total,
            });
          }),
          catchError((error) =>
            of(
              SubmissionActions.getSubmissionSitesFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getSitesForSubmission$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.getSitesForWorkspace),
      switchMap(({ submissionId, queryParams }) =>
        this._siteService.getSubmissionSites(submissionId, queryParams).pipe(
          map((response) => {
            return SubmissionActions.getStudySitesSuccess({
              sites: convertKeysToCamelCase(response.data),
              statuses: response.statuses,
              total: response.total,
            });
          }),
          catchError((error) =>
            of(
              SubmissionActions.getSubmissionSitesFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  removeSitesFromSubmission$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.removeSitesFromSubmission),
      switchMap(({ submissionId, siteIds, reason }) =>
        this._siteService
          .removeSitesFromSubmission(submissionId, siteIds, reason)
          .pipe(
            map(() => SubmissionActions.removeSitesFromSubmissionSuccess()),
            catchError((error) =>
              of(SubmissionActions.removeSitesFromSubmissionFailure({ error })),
            ),
          ),
      ),
    ),
  );

  irbPriorityDialog$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.irbPriorityChange),
      switchMap((action) =>
        this._submissionsService
          .updateIRBStatus(action.workspaceId, action.priority)
          .pipe(
            switchMap((response) => {
              return [
                SUBMISSION_HEADER_ACTIONS.updateIRBPlus({
                  irbState: (response as SubmissionResponse)
                    .priority as IRBPlus,
                  isAPI: true,
                }),
              ];
            }),
            catchError((error) =>
              of(SubmissionActions.irbPriorityChangeFailure({ error })),
            ),
          ),
      ),
    ),
  );

  acknowledgeFailedDocuments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SubmissionActions.acknowledgeFailedDocuments),
      switchMap(({ workspaceId, documentIds }) =>
        this._submissionsService
          .acknowledgeFailedDocuments(workspaceId, documentIds)
          .pipe(
            switchMap(() => {
              return [
                SubmissionActions.onAcknowledgeFailedDocumentsSuccess({
                  closeBanner: true,
                }),
              ];
            }),
            catchError((error) =>
              of(
                SubmissionActions.onAcknowledgeFailedDocumentsFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submissionHeaderDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SubmissionActions.submissionHeaderDialog),
        tap(({ component, data }) => {
          this._matDialog.open(component(), {
            data,
          });
        }),
      ),
    { dispatch: false },
  );

  private _handleSubmissionAndCallService<T>(
    action: any,
    serviceCall: (submissionId: string, action: any) => Observable<T>,
    successAction: (response: T, action: any) => any,
    failureAction: (error: any) => any,
  ) {
    return this._store.select(selectSubmission).pipe(
      filter((submission): submission is Submission => !!submission),
      take(1), // Take only the latest submission once
      mergeMap((submission) =>
        serviceCall(submission.id, action).pipe(
          map((response) => successAction(response, action)),
          catchError((error) => of(failureAction(error))),
        ),
      ),
    );
  }
}
